import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./languageToggle.css";

const LANGUAGES = [
  { code: "en", label: "EN", name: "English", command: "set-lang en" },
  { code: "zh", label: "简中", name: "简体中文", command: "set-lang zh" },
  {
    code: "zh-Hant",
    label: "繁中",
    name: "繁體中文",
    command: "set-lang zh-Hant",
  },
];

const TYPE_DELAY = 55;
const ERASE_DELAY = 30;

const LanguageToggle = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [typedLabel, setTypedLabel] = useState("");
  const containerRef = useRef(null);
  const timeoutsRef = useRef([]);
  const currentTextRef = useRef("");
  const isFirstRender = useRef(true);

  const currentLanguage =
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const clearTimers = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const setText = (text) => {
    currentTextRef.current = text;
    setTypedLabel(text);
  };

  const typeLabel = (target) => {
    clearTimers();

    const typeStep = (index) => {
      setText(target.slice(0, index));
      if (index >= target.length) return;
      timeoutsRef.current.push(
        setTimeout(() => typeStep(index + 1), TYPE_DELAY),
      );
    };

    const eraseStep = (text) => {
      if (text.length === 0) {
        typeStep(0);
        return;
      }
      setText(text);
      timeoutsRef.current.push(
        setTimeout(() => eraseStep(text.slice(0, -1)), ERASE_DELAY),
      );
    };

    eraseStep(currentTextRef.current);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setText(currentLanguage.label);
    }
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectLanguage = (lang) => {
    setIsOpen(false);
    if (lang.code === currentLanguage.code) return;
    i18n.changeLanguage(lang.code);
    localStorage.setItem("language", lang.code);
    typeLabel(lang.label);
  };

  return (
    <div className="language-dropdown" ref={containerRef}>
      <button
        className="language-toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Change language"
        aria-expanded={isOpen}>
        <span className="language-toggle-prompt">{">"}</span>
        <span className="language-toggle-text">{typedLabel}</span>
        <span className="language-toggle-cursor">_</span>
      </button>
      <div className={`language-menu ${isOpen ? "visible" : ""}`}>
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            className={`language-option ${
              lang.code === currentLanguage.code ? "active" : ""
            }`}
            onClick={() => selectLanguage(lang)}>
            <span className="language-option-command">$ {lang.command}</span>
            <span className="language-option-name">{lang.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageToggle;
